import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Container, Badge, Modal, Form } from "react-bootstrap";
import classes from "../WebsiteManagement/websiteManagement.module.css";
import Page from "../pagination/pagination";
import {
  GetAdminTrainBookings,
  DownloadTrainTicketPdfById,
  DownloadTrainInvoicePdfById,
  CompleteTrainCancellation,
} from "../../entities/action/action";

const PAGE_SIZE = 50;

const thStyle = { padding: "10px 6px", textAlign: "left", fontSize: 12, fontWeight: 600, borderBottom: "2px solid #ccc", whiteSpace: "nowrap" };
const tdStyle = { padding: "10px 6px", fontSize: 12, borderBottom: "1px solid #eee", verticalAlign: "top" };

const formatStation = (name, code) => {
  const n = String(name || "").trim();
  const c = String(code || "").trim();
  if (n && c) return `${n} (${c})`;
  return n || c || "—";
};

const TrainBookingRow = ({ item, onCompleted }) => {
  const orderDate = new Date(item?.orderDate);
  const [showModal, setShowModal] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  const pendingCancellation = String(item?.cancellationStatus || "").toUpperCase() === "PENDING";
  const cancellationSuccess = String(item?.cancellationStatus || "").toUpperCase() === "CANCELLATION_SUCCESS";
  const downloadRef = item?.pnr || item?.bookingId || item?.id;

  const completeCancellation = async () => {
    if (!item?.cancellationRequestId) return;
    setLoading(true);
    try {
      const res = await CompleteTrainCancellation(item.cancellationRequestId, adminRemarks);
      if (res?.success) {
        setShowModal(false);
        setAdminRemarks("");
        if (onCompleted) onCompleted();
      } else {
        alert(res?.message || "Failed to update cancellation status.");
      }
    } finally {
      setLoading(false);
    }
  };

  const passengers = (item.passengers || []).map((p) => p.passengerName).filter(Boolean).join(", ");
  const statusBg = pendingCancellation ? "#ffc107" : cancellationSuccess ? "#198754" : "#0dcaf0";
  const statusColor = cancellationSuccess ? "#fff" : "#000";
  const statusText = pendingCancellation ? "PENDING CANCELLATION" : cancellationSuccess ? "CANCELLATION SUCCESS" : (item?.status || "").toUpperCase();

  return (
    <>
      <tr style={{ backgroundColor: pendingCancellation ? "#fff8e1" : "#f9f9f9", border: pendingCancellation ? "1px solid #ffc107" : "none" }}>
        <td style={tdStyle}>
          {orderDate.getDate()}/{orderDate.getMonth() + 1}/{orderDate.getFullYear()}
          <div style={{ color: "gray" }}>{orderDate.toLocaleTimeString()}</div>
        </td>
        <td style={tdStyle}>
          {formatStation(item.boardingStation, item.boardingStationCode)}
          <div style={{ color: "gray" }}>{item.departureDate || item.journeyDate || ""}</div>
          {item.departureTime && <div style={{ color: "gray" }}>{item.departureTime}</div>}
        </td>
        <td style={tdStyle}>
          {formatStation(item.destinationStation, item.destinationStationCode)}
          {item.arrivalDate && <div style={{ color: "gray" }}>{item.arrivalDate}</div>}
          {item.arrivalTime && <div style={{ color: "gray" }}>{item.arrivalTime}</div>}
        </td>
        <td style={tdStyle}>
          <div>{item.customerName || "—"}</div>
          <div style={{ color: "gray" }}>{item.customerMobile || "—"}</div>
          {passengers && <div style={{ color: "gray", fontSize: 11 }}>{passengers}</div>}
        </td>
        <td style={tdStyle}>
          <div>{item.pnr || "—"}</div>
          <div style={{ color: "gray" }}>{[item.trainNumber, item.trainName].filter(Boolean).join(" · ") || ""}</div>
        </td>
        <td style={tdStyle}>—</td>
        <td style={tdStyle}>
          {item.bookingId || "—"}
          {pendingCancellation && (
            <div style={{ marginTop: 4 }}>
              <Badge bg="warning" text="dark" style={{ fontSize: 10 }}>Cancellation requested</Badge>
              {item.cancellationRemarks && <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>Remark: {item.cancellationRemarks}</div>}
            </div>
          )}
        </td>
        <td style={tdStyle}>INR {Number(item.amount || 0).toLocaleString("en-IN")}</td>
        <td style={tdStyle}>{item.purchaseAmount ? `INR ${Number(item.purchaseAmount).toLocaleString("en-IN")}` : "—"}</td>
        <td style={tdStyle}>
          <Button size="sm" variant="outline-primary" style={{ fontSize: 10, marginBottom: 5, whiteSpace: "nowrap" }} onClick={() => DownloadTrainTicketPdfById(item.id, downloadRef)}>
            Download Ticket
          </Button>
          <Button size="sm" variant="outline-secondary" style={{ fontSize: 10, marginBottom: 5, whiteSpace: "nowrap" }} onClick={() => DownloadTrainInvoicePdfById(item.id, downloadRef)}>
            Download Invoice
          </Button>
          {pendingCancellation && (
            <Button size="sm" variant="success" style={{ fontSize: 10, marginBottom: 5, whiteSpace: "nowrap" }} onClick={() => setShowModal(true)}>
              Mark cancellation success
            </Button>
          )}
          <div style={{ background: statusBg, color: statusColor, padding: 4, marginTop: 5, textAlign: "center", fontSize: 10 }}>
            {statusText}
          </div>
        </td>
      </tr>

      <Modal show={showModal} onHide={() => !loading && setShowModal(false)}>
        <Modal.Header closeButton><Modal.Title>Complete train cancellation</Modal.Title></Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14 }}>Confirm that you have manually cancelled this train booking with the railway / provider.</p>
          {item.cancellationRemarks && <p style={{ fontSize: 13, color: "#666" }}><strong>User remark:</strong> {item.cancellationRemarks}</p>}
          <Form.Group>
            <Form.Label>Admin remarks (optional)</Form.Label>
            <Form.Control as="textarea" rows={3} value={adminRemarks} onChange={(e) => setAdminRemarks(e.target.value)} placeholder="Internal note about the cancellation" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>Close</Button>
          <Button variant="success" onClick={completeCancellation} disabled={loading}>{loading ? "Saving…" : "Mark cancellation success"}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const TrainBookings = () => {
  const [all, setAll] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoadError("");
    try {
      const res = await GetAdminTrainBookings();
      if (res?.success && Array.isArray(res.bookings)) {
        setAll(res.bookings);
      } else {
        setAll([]);
        setLoadError(res?.message || "Failed to load train bookings.");
      }
    } catch {
      setAll([]);
      setLoadError("Failed to load train bookings.");
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search, pendingOnly]);

  const filtered = useMemo(() => {
    let rows = all;
    if (pendingOnly) rows = rows.filter((b) => String(b.cancellationStatus || "").toUpperCase() === "PENDING");
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) => {
      const name = String(b.customerName || "").toLowerCase();
      const phone = String(b.customerMobile || "").toLowerCase();
      const pnr = String(b.pnr || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || pnr.includes(q);
    });
  }, [all, search, pendingOnly]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Fragment>
      <Container style={{ display: "flex", justifyContent: "center", margin: "20px 0", gap: 12, flexWrap: "wrap" }}>
        <form onSubmit={(e) => e.preventDefault()} style={{ display: "flex", alignItems: "stretch", width: "50vw", minWidth: 280 }}>
          <input className={classes.SearchInput} placeholder="Search by name, phone, or PNR" value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setSearch(e.target.value); }} />
          <button type="button" className={classes.SearchButton} onClick={() => setSearch(searchInput)}>Search</button>
        </form>
        <Button variant={pendingOnly ? "warning" : "outline-warning"} size="sm" onClick={() => setPendingOnly((v) => !v)}>
          {pendingOnly ? "Showing pending cancellations" : "Show pending cancellations only"}
        </Button>
      </Container>
      {loadError && <p style={{ color: "#b91c1c", textAlign: "center", margin: "12px 0" }}>{loadError}</p>}
      {pageItems.length ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
            <thead>
              <tr style={{ backgroundColor: "#585858", color: "#fff" }}>
                <th style={thStyle}>Booking Date & Time</th>
                <th style={thStyle}>Departure & Boarding Point</th>
                <th style={thStyle}>Arrival City</th>
                <th style={thStyle}>Traveller's Name & Details</th>
                <th style={thStyle}>PNR & Train Details</th>
                <th style={thStyle}>Seat Details</th>
                <th style={thStyle}>Reference ID</th>
                <th style={thStyle}>Base Amount</th>
                <th style={thStyle}>Purchase Amount</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => <TrainBookingRow key={item.id} item={item} onCompleted={load} />)}
            </tbody>
          </table>
        </div>
      ) : (
        !loadError && <p>No train bookings found</p>
      )}
      {totalPages > 1 && <Page count={totalPages} page={page} change={(_, v) => setPage(v)} />}
    </Fragment>
  );
};

export default TrainBookings;
